import { MixingWork } from '@twinsketch/topika-model';
import React, { useState } from 'react';

interface NMPConsumptionChartProps {
  mixingWorks: MixingWork[];
}

const NMPConsumptionChart: React.FC<NMPConsumptionChartProps> = ({ mixingWorks }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Calculate NMP consumption for each batch
  const batchData = mixingWorks.map(work => {
    // Find all NMP-related steps (commonly named "Add NMP", "NMP", etc.)
    const nmpSteps = work.steps.filter(step => step.materialName.toLowerCase().includes('nmp'));

    const totalNMP = nmpSteps.reduce((sum, step) => sum + step.amountInKg, 0);

    // Calculate NMP per kg of active material (efficiency metric)
    // Assuming active material is the largest material addition
    const activeMaterialStep = work.steps.reduce(
      (max, step) => (step.amountInKg > max.amountInKg ? step : max),
      work.steps[0]
    );

    const nmpPerKgActive =
      activeMaterialStep.amountInKg > 0 ? totalNMP / activeMaterialStep.amountInKg : 0;

    return {
      lotNumber: work.lotNumber,
      date: work.date,
      totalNMP,
      nmpSteps,
      activeMaterialKg: activeMaterialStep.amountInKg,
      nmpPerKgActive,
      finalViscosity: work.measuredViscosityCps,
      finalSolidContent: work.measuredSolidContentPercent,
    };
  });

  if (batchData.length === 0 || batchData.every(b => b.totalNMP === 0)) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '48rem',
          margin: '0 auto',
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          NMP Consumption Variance
        </h3>
        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No NMP consumption data available</p>
      </div>
    );
  }

  // Calculate statistics
  const avgNMP = batchData.reduce((sum, b) => sum + b.totalNMP, 0) / batchData.length;
  const maxNMP = Math.max(...batchData.map(b => b.totalNMP));
  const minNMP = Math.min(...batchData.map(b => b.totalNMP));
  const variance = maxNMP - minNMP;
  const variancePercent = minNMP > 0 ? (variance / minNMP) * 100 : 0;

  // Dynamic label configuration based on data density
  const dataCount = batchData.length;
  const shouldRotate = dataCount > 5;

  // Chart dimensions
  const chartHeight = 320;
  const minChartWidth = 400;
  const barWidthIdeal = 15;
  const gapBetweenBars = 5;
  const totalBarsWidth = dataCount * (barWidthIdeal + gapBetweenBars);
  const chartWidth = Math.max(minChartWidth, totalBarsWidth + 240);

  // Dynamic bottom padding based on rotation
  const bottomPadding = shouldRotate ? 100 : 60;
  const padding = {
    top: 40,
    right: 140,
    bottom: bottomPadding,
    left: 70,
  };

  const plotHeight = chartHeight - padding.top - padding.bottom;
  const plotWidth = chartWidth - padding.left - padding.right;

  // Scale function
  const chartMax = Math.ceil(maxNMP / 50) * 50 + 50;
  const scaleY = (value: number) => {
    return plotHeight - (value / chartMax) * plotHeight;
  };

  // Bar configuration
  const actualBarWidth = Math.min(
    barWidthIdeal,
    (plotWidth - (dataCount - 1) * gapBetweenBars) / dataCount
  );
  const barWithGap = actualBarWidth + gapBetweenBars;

  // Y-axis ticks
  const yTicks: number[] = [];
  const tickInterval = chartMax <= 100 ? 20 : 50;
  for (let i = 0; i <= chartMax; i += tickInterval) {
    yTicks.push(i);
  }

  // Determine if variance is significant (>10% difference)
  const isSignificantVariance = variancePercent > 10;

  // Color based on variance from average
  const getBarColor = (nmpAmount: number): string => {
    const deviationPercent = Math.abs((nmpAmount - avgNMP) / avgNMP) * 100;
    if (deviationPercent > 15) return '#ef4444'; // red - high deviation
    if (deviationPercent > 5) return '#f59e0b'; // amber - moderate deviation
    return '#10b981'; // green - close to average
  };

  return (
    <div className="chart-card">
      <div>
        <h3>NMP Consumption Variance</h3>
        <span className="chart-helper-text">Hover over bars for details</span>
      </div>
      {/* Summary Stats */}
      <div
        style={{
          display: 'inline-grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginBottom: '1rem',
          padding: '0.75rem',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.625rem', color: '#4b5563', marginBottom: '0.125rem' }}>
            Average
          </p>
          <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
            {avgNMP.toFixed(1)} kg
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.625rem', color: '#4b5563', marginBottom: '0.125rem' }}>
            Variance
          </p>
          <p
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: isSignificantVariance ? '#f59e0b' : '#059669',
            }}
          >
            {variance.toFixed(1)} kg ({variancePercent.toFixed(1)}%)
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.625rem', color: '#4b5563', marginBottom: '0.125rem' }}>Range</p>
          <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
            {minNMP.toFixed(1)} - {maxNMP.toFixed(1)} kg
          </p>
        </div>
      </div>

      <div className="chart-container">
        <svg width={chartWidth} height={chartHeight} style={{ fontFamily: 'sans-serif' }}>
          {/* Legend at top right - positioned above the plot area */}
          <g>
            {/* Legend background */}
            <rect
              x={chartWidth - padding.right + 10}
              y={5}
              width={145}
              height={70}
              fill="white"
              stroke="#e5e7eb"
              strokeWidth="1"
              rx="4"
              opacity="0.95"
            />

            {/* Within 5% */}
            <rect
              x={chartWidth - padding.right + 18}
              y={13}
              width={12}
              height={12}
              fill="#10b981"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={19}
              fontSize="10"
              fill="#374151"
              dominantBaseline="middle"
            >
              Within 5% of avg
            </text>

            {/* 5-15% deviation */}
            <rect
              x={chartWidth - padding.right + 18}
              y={33}
              width={12}
              height={12}
              fill="#f59e0b"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={39}
              fontSize="10"
              fill="#374151"
              dominantBaseline="middle"
            >
              5-15% deviation
            </text>

            {/* >15% deviation */}
            <rect
              x={chartWidth - padding.right + 18}
              y={53}
              width={12}
              height={12}
              fill="#ef4444"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={59}
              fontSize="10"
              fill="#374151"
              dominantBaseline="middle"
            >
              &gt;15% deviation
            </text>
          </g>

          {/* Average line */}
          <line
            x1={padding.left}
            y1={scaleY(avgNMP) + padding.top}
            x2={padding.left + plotWidth}
            y2={scaleY(avgNMP) + padding.top}
            stroke="#3b82f6"
            strokeWidth="2"
            strokeDasharray="6,3"
            opacity="0.6"
          />
          <text
            x={padding.left + plotWidth + 5}
            y={scaleY(avgNMP) + padding.top}
            fontSize="10"
            fill="#1e40af"
            dominantBaseline="middle"
          >
            Avg: {avgNMP.toFixed(1)} kg
          </text>

          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Y-axis ticks and labels */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line
                x1={padding.left - 5}
                y1={scaleY(tick) + padding.top}
                x2={padding.left}
                y2={scaleY(tick) + padding.top}
                stroke="#374151"
                strokeWidth="2"
              />
              <text
                x={padding.left - 10}
                y={scaleY(tick) + padding.top}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12"
                fill="#374151"
              >
                {tick}
              </text>
            </g>
          ))}

          {/* Y-axis label */}
          <text
            x={20}
            y={padding.top + plotHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            transform={`rotate(-90, 20, ${padding.top + plotHeight / 2})`}
          >
            NMP (kg)
          </text>

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={chartHeight - padding.bottom}
            x2={chartWidth - padding.right}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Bars and labels */}
          {batchData.map((batch, index) => {
            const xPosition = padding.left + index * barWithGap;
            const barHeight = plotHeight - scaleY(batch.totalNMP);
            const yPosition = scaleY(batch.totalNMP) + padding.top;
            const barColor = getBarColor(batch.totalNMP);
            const deviationPercent = Math.abs((batch.totalNMP - avgNMP) / avgNMP) * 100;

            const labelX = xPosition + actualBarWidth / 2;
            const labelStartY = chartHeight - padding.bottom + 15;

            // Tooltip dimensions
            const tooltipWidth = 200;
            const tooltipHeight = 130;
            const tooltipPadding = 12;

            // Calculate tooltip position
            let tooltipX = xPosition + actualBarWidth / 2 - tooltipWidth / 2;
            if (tooltipX < padding.left) {
              tooltipX = padding.left + 5;
            } else if (tooltipX + tooltipWidth > chartWidth - padding.right) {
              tooltipX = chartWidth - padding.right - tooltipWidth - 5;
            }

            const tooltipY = yPosition - tooltipHeight - 15;

            return (
              <g key={batch.lotNumber}>
                {/* Bar */}
                <rect
                  x={xPosition}
                  y={yPosition}
                  width={actualBarWidth}
                  height={barHeight}
                  fill={barColor}
                  opacity={hoveredIndex === index ? 1 : 0.8}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />

                {/* Tooltip on hover */}
                {hoveredIndex === index && (
                  <>
                    {/* Tooltip background with shadow */}
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipWidth}
                      height={tooltipHeight}
                      fill="white"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                      rx="6"
                      style={{
                        pointerEvents: 'none',
                        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                      }}
                    />

                    {/* Tooltip content */}
                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 22}
                      textAnchor="start"
                      fontSize="12"
                      fill="#374151"
                      fontWeight="700"
                      style={{ pointerEvents: 'none' }}
                    >
                      {batch.lotNumber}
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 40}
                      textAnchor="start"
                      fontSize="11"
                      fill="#6b7280"
                      style={{ pointerEvents: 'none' }}
                    >
                      Date:{' '}
                      {new Date(batch.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 58}
                      textAnchor="start"
                      fontSize="12"
                      fill={barColor}
                      fontWeight="700"
                      style={{ pointerEvents: 'none' }}
                    >
                      Total NMP: {batch.totalNMP.toFixed(1)} kg
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 76}
                      textAnchor="start"
                      fontSize="10"
                      fill="#6b7280"
                      style={{ pointerEvents: 'none' }}
                    >
                      Efficiency: {batch.nmpPerKgActive.toFixed(3)} kg/kg
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 94}
                      textAnchor="start"
                      fontSize="10"
                      fill={barColor}
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      Deviation: {batch.totalNMP > avgNMP ? '+' : ''}
                      {(batch.totalNMP - avgNMP).toFixed(1)} kg
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 112}
                      textAnchor="start"
                      fontSize="10"
                      fill="#4b5563"
                      style={{ pointerEvents: 'none' }}
                    >
                      Final: {batch.finalViscosity.toLocaleString()} cps
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Insights */}
      <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {isSignificantVariance && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '0.5rem',
            }}
          >
            <h4
              style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#78350f',
                marginBottom: '0.5rem',
              }}
            >
              ⚠️ Significant Variance Detected
            </h4>
            <p style={{ fontSize: '0.875rem', color: '#92400e' }}>
              NMP consumption varies by {variance.toFixed(1)} kg ({variancePercent.toFixed(1)}%)
              between batches. This represents a cost optimization opportunity.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NMPConsumptionChart;
